import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import '../../assets/css/Login.css';

const BrandMark = () => (
	<svg viewBox="0 0 38 38" aria-hidden="true" focusable="false">
		<circle cx="19" cy="19" r="12.5" fill="none" stroke="currentColor" strokeWidth="3" />
		<circle cx="19" cy="19" r="6" fill="none" stroke="currentColor" strokeWidth="3" />
		<circle cx="19" cy="19" r="2.5" fill="currentColor" />
	</svg>
);

const ArrowIcon = () => (
	<svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">
		<path d="M4 10h11M11 6l4 4-4 4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
	</svg>
);

const EyeIcon = ({ hidden }) => hidden ? (
	<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
		<path d="M10.6 10.6a2 2 0 0 0 2.8 2.8M16.7 16.7A8.8 8.8 0 0 1 12 18c-3.6 0-6.6-2-9-6a15.3 15.3 0 0 1 4.3-4.7m2.9-1.1A9 9 0 0 1 12 6c3.6 0 6.6 2 9 6a15.5 15.5 0 0 1-2.1 2.9M3 3l18 18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
	</svg>
) : (
	<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
		<path d="M10 12a2 2 0 1 0 4 0 2 2 0 0 0-4 0M21 12c-2.4 4-5.4 6-9 6s-6.6-2-9-6c2.4-4 5.4-6 9-6s6.6 2 9 6" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
	</svg>
);

const LockIcon = () => (
	<svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">
		<rect x="4" y="8" width="12" height="9" rx="2" fill="none" stroke="currentColor" strokeWidth="1.6" />
		<path d="M7 8V6a3 3 0 0 1 6 0v2" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
	</svg>
);

const AlertIcon = () => (
	<svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">
		<circle cx="10" cy="10" r="7.2" fill="none" stroke="currentColor" strokeWidth="1.6" />
		<path d="M10 6.3v4.3M10 13.7h.01" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
	</svg>
);

const Login = () => {
	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');
	const [error, setError] = useState('');
	const [showPassword, setShowPassword] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const { login } = useAuth();
	const navigate = useNavigate();

	const handleSubmit = async (event) => {
		event.preventDefault();
		if (isSubmitting) return;

		setError('');
		setIsSubmitting(true);

		try {
			await login(email, password);
			navigate('/games');
		} catch (loginError) {
			setError(loginError?.message || 'Invalid credentials');
			setIsSubmitting(false);
		}
	};

	return (
		<div className="club-login-page">
			<header className="club-login-header">
				<div className="club-login-container club-login-header-inner">
					<Link to="/" className="club-login-brand" aria-label="CueClub Manager home">
						<span className="club-login-brand-mark"><BrandMark /></span>
						<span className="club-login-brand-copy">
							<strong>CueClub Manager</strong>
							<small>Club operations</small>
						</span>
					</Link>

					<Link to="/" className="club-login-back">
						<span>Back to website</span>
						<ArrowIcon />
					</Link>
				</div>
			</header>

			<main className="club-login-main">
				<div className="club-login-container club-login-layout">
					<section className="club-login-showcase" aria-labelledby="club-login-showcase-title">
						<div className="club-login-showcase-copy">
							<p className="club-login-kicker"><i /> Your club, in sync</p>
							<h1 id="club-login-showcase-title">One calm view of a busy room.</h1>
							<p>Tables, matches, queues and live screens stay connected from opening time to the final frame.</p>
						</div>

						<div className="club-login-floor" aria-label="Example of a live club floor">
							<div className="club-login-floor-top">
								<span>Tonight&apos;s floor</span>
								<small><i /> Live room</small>
							</div>

							<div className="club-login-floor-summary">
								<div><small>TABLES IN PLAY</small><strong>08</strong></div>
								<div><small>PLAYERS WAITING</small><strong>04</strong></div>
							</div>

							<div className="club-login-floor-list">
								<article>
									<span className="is-playing">01</span>
									<div><strong>Table 01</strong><small>Youssef vs Adam</small></div>
									<em>IN PLAY</em>
								</article>
								<article>
									<span className="is-waiting">02</span>
									<div><strong>Table 02</strong><small>3 players in queue</small></div>
									<em className="is-next">NEXT</em>
								</article>
								<article>
									<span className="is-open">03</span>
									<div><strong>Table 03</strong><small>Ready for a game</small></div>
									<em className="is-ready">OPEN</em>
								</article>
							</div>
						</div>

						<p className="club-login-showcase-note"><span>✓</span> Everything your team needs, without the noise.</p>
					</section>

					<section className="club-login-panel" aria-labelledby="club-login-title">
						<div className="club-login-panel-heading">
							<p>Club login</p>
							<h2 id="club-login-title">Welcome back.</h2>
							<span>Sign in to open your club workspace.</span>
						</div>

						<form className="club-login-form" onSubmit={handleSubmit} noValidate={false}>
							<div className="club-login-field">
								<label htmlFor="club-login-email">Email address</label>
								<input
									id="club-login-email"
									type="email"
									placeholder="club@example.com"
									value={email}
									onChange={(event) => setEmail(event.target.value)}
									autoComplete="email"
									aria-describedby={error ? 'club-login-error' : undefined}
									required
								/>
							</div>

							<div className="club-login-field">
								<label htmlFor="club-login-password">Password</label>
								<div className="club-login-password">
									<input
										id="club-login-password"
										type={showPassword ? 'text' : 'password'}
										placeholder="Enter your password"
										value={password}
										onChange={(event) => setPassword(event.target.value)}
										autoComplete="current-password"
										aria-describedby={error ? 'club-login-error' : undefined}
										required
									/>
									<button
										type="button"
										className="club-login-password-toggle"
										aria-label={showPassword ? 'Hide password' : 'Show password'}
										aria-pressed={showPassword}
										onClick={() => setShowPassword((visible) => !visible)}
									>
										<EyeIcon hidden={showPassword} />
									</button>
								</div>
							</div>

							{error && (
								<div id="club-login-error" className="club-login-error" role="alert" aria-live="polite">
									<AlertIcon />
									<span>{error}</span>
								</div>
							)}

							<button type="submit" className="club-login-submit" disabled={isSubmitting} aria-busy={isSubmitting}>
								{isSubmitting ? (
									<><span className="club-login-spinner" aria-hidden="true" /> Signing in…</>
								) : (
									<>Sign in to CueClub <ArrowIcon /></>
								)}
							</button>

							<p className="club-login-security"><LockIcon /> Secure access to your club&apos;s private workspace</p>
						</form>

						<div className="club-login-help">
							<strong>Need access?</strong>
							<span>Ask your club administrator to create or update your account.</span>
						</div>
					</section>
				</div>
			</main>
		</div>
	);
};

export default Login;
